import { AxiosError } from 'axios'
import { ENDPOINT_PROJECT_STATUSES } from '../config'
import type { ProjectStatusMembershipResponse } from '../types'
import AuthAxios from '@/lib/api/axios/AuthAxios'

/**
 * Attach a workspace status to a specific project, making it visible in that
 * project's status picker.
 *
 * Backend: `POST /api/v1.0/e/{workspaceSlug}/projects/{projectId}/statuses/{statusId}`
 *
 * No-op cases (still return 200 with changed=false):
 * - Status is global (no WorkspaceStatusProject rows) → already visible everywhere.
 * - Status is already attached to this project.
 *
 * Errors:
 * - 400 when trying to attach a system-generated (BL/CD) status — those are always global.
 * - 403 when the caller lacks ManageWorkspaceSettings.
 * - 404 when workspace / project / status is not found.
 */
const attachStatusToProject = async (
  workspaceSlug: string,
  projectId: string,
  statusId: string,
): Promise<ProjectStatusMembershipResponse> => {
  try {
    const response = await AuthAxios.post<ProjectStatusMembershipResponse>(
      `${ENDPOINT_PROJECT_STATUSES}/${workspaceSlug}/projects/${projectId}/statuses/${statusId}`,
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError) {
      if (error.response?.status === 403) {
        throw new Error('You do not have permission to manage statuses for this project')
      }
      if (error.response?.status === 400 && typeof error.response.data?.message === 'string') {
        throw new Error(error.response.data.message)
      }
    }
    throw error
  }
}

export default attachStatusToProject
